import type { ReactNode } from "react";
import { parseBlockContent } from "../../../lib/blockContentSchemas";
import { BLOCK_FLOW_CLASS, flowAttributes } from "../document/blockFlow";
import { columnValign } from "../document/documentModel";
import { RichText } from "../document/RichText";
import type { EditorAsset, EditorBlock, EditorPage } from "../editor.types";
import { BlockRenderer } from "./BlockRenderer";
import { sectionAnchor } from "./buttonSettings";
import { sectionBackground, sectionBackgroundAttributes } from "./siteBackgrounds";

type SiteBlockListProps = {
  /** Every block on the page; the list shows those directly under `parent`. */
  blocks: EditorBlock[];
  parent: string | null;
  assetsById: Map<string, EditorAsset>;
  pages: EditorPage[];
  currentPageId: string;
  onOpenPage: (page: EditorPage) => void;
  onSelectPage: (pageId: string) => void;
  pageHref: (page: EditorPage) => string;
};

/**
 * A published page's blocks, as the live site shows them. Text sits directly
 * in the flow and other blocks are labelled, matching the editor canvas so
 * blockFlow.css spaces both alike; sections, and each side of columns, hold a
 * list of their own.
 */
export function SiteBlockList({ blocks, parent, ...props }: SiteBlockListProps): ReactNode {
  return blocks
    .filter((block) => (block.parent_source_block_id ?? null) === parent)
    .sort((a, b) => a.position - b.position)
    .map((block) => {
      if (block.block_type === "text")
        return (
          <RichText
            key={block.id}
            flat
            value={parseBlockContent("text", block.content).richText}
          />
        );
      if (block.block_type === "section") {
        const { width, spacing, align } = parseBlockContent("section", block.content);
        const { class: className, ...background } = sectionBackgroundAttributes(
          sectionBackground(block.content),
        );
        return (
          <section
            key={block.id}
            // A button on the page can scroll here (sectionAnchor).
            id={sectionAnchor(block.source_block_id)}
            className={className}
            {...flowAttributes(block)}
            {...background}
            data-width={width}
            data-spacing={spacing}
            data-align={align}
          >
            <div className={`site-section-content ${BLOCK_FLOW_CLASS}`}>
              <SiteBlockList blocks={blocks} parent={block.source_block_id} {...props} />
            </div>
          </section>
        );
      }
      if (block.block_type === "columns")
        return (
          <div key={block.id} className="site-columns" {...flowAttributes(block)}>
            {blocks
              .filter(
                (column) =>
                  column.parent_source_block_id === block.source_block_id &&
                  column.block_type === "column",
              )
              .sort((a, b) => a.position - b.position)
              .map((column) => (
                <div
                  key={column.id}
                  className={`site-column ${BLOCK_FLOW_CLASS}`}
                  data-valign={columnValign(column, block)}
                >
                  <SiteBlockList blocks={blocks} parent={column.source_block_id} {...props} />
                </div>
              ))}
          </div>
        );
      return (
        <div key={block.id} className="document-structured" {...flowAttributes(block)}>
          <BlockRenderer bare block={block} blocks={blocks} {...props} />
        </div>
      );
    });
}
